'use client'

import React from 'react'
import { Page } from '@/payload-types'
import { CMSLink } from '@/components/Link'
import { RichText } from '@/components/RichText'
import { AnimatedHeading } from '@/components/AnimatedHeading'
import { Button } from '@/components/ui/button'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import { useTheme } from '@/providers/Theme'
import classes from './index.module.css'

type HeroProps = {
  type: 'customHero'
  content?: {
    heading?: string
    useAnimatedHeading?: boolean
    textColor?: 'white' | 'emerald' | 'dark'
    description?: any
    links?: Array<{ link: any }>
    media?: number | any // Adjust type as needed
  }
}

// Function to parse heading for line breaks
const renderHeadingWithLineBreaks = (heading: string, className: string) => {
  const lines = heading.split('|').map((line) => line.trim())
  return (
    <h1 className={className}>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </h1>
  )
}

// Helper to deeply inspect Payload RichText objects (Lexical/Slate) to see if they contain actual text
const isRichTextEmpty = (richText: any) => {
  if (!richText) return true
  try {
    const str = JSON.stringify(richText)
    const textMatches = Array.from(str.matchAll(/"text":"(.*?)"/g))
    // It's empty if there are no text nodes, or if all text nodes contain only whitespace
    return textMatches.length === 0 || textMatches.every(match => match[1].trim() === '')
  } catch (e) {
    return false // Fallback
  }
}

export const CustomHero: React.FC<Page['hero']> = (props) => {
  const heroProps = props as unknown as HeroProps
  const content = heroProps?.content
  const t = useTranslations('common')
  const { theme } = useTheme()

  // Dynamic colors based on theme
  // Theme is already resolved by the provider (handles 'auto' mode via getImplicitPreference)
  // Default to light mode if theme is undefined
  const isDark = theme === 'dark'
  const baseColor = isDark ? '#374151' : '#064e3b' // Dark gray base for dark mode (as per original), Deep Emerald for light

  const waveColors = isDark
    ? ['#8B4513', '#CD853F', '#D2691E', '#B8860B'] // Bronze palette (original)
    : ['#059669', '#10b981', '#34d399', '#6ee7b7'] // Emerald palette

  const glowColors = isDark
    ? [
      'rgba(139, 69, 19, 0.3)',
      'rgba(205, 133, 63, 0.4)',
      'rgba(210, 105, 30, 0.5)',
      'rgba(184, 134, 11, 0.4)',
    ]
    : [
      'rgba(5, 150, 105, 0.3)',
      'rgba(16, 185, 129, 0.4)',
      'rgba(52, 211, 153, 0.5)',
      'rgba(110, 231, 183, 0.4)',
    ]

  if (!content) return null

  const hasHeading = !!content?.heading?.trim()
  const hasDescription = !isRichTextEmpty(content?.description)
  const hasLinks = content?.links && content.links.length > 0

  // Collapse empty hero sections completely so they don't produce giant layout gaps
  if (!hasHeading && !hasDescription && !hasLinks) {
    return null
  }

  return (
    <section className={classes.hero}>
      <div className={classes.heroWrapper}>
        <div className={classes.content}>
          {hasHeading &&
            (content?.useAnimatedHeading ? (
              <div className="animatedHeadingContainer">
                {content.heading!
                  .split('|')
                  .map((line) => line.trim())
                  .map((line, index) => (
                    <AnimatedHeading
                      key={index}
                      text={line}
                      color={baseColor}
                      waveColors={waveColors}
                      glowColors={glowColors}
                    />
                  ))}
              </div>
            ) : (
              renderHeadingWithLineBreaks(content.heading as string, classes.staticHeading)
            ))}

          {hasDescription && (
            <RichText
              data={content.description}
              className={classes.description}
              enableGutter={false}
              enableProse={false}
            />
          )}

          {/* CMS Links if defined */}
          {hasLinks && (
            <div className={classes.buttons}>
              {content.links!.map(({ link }, index: number) => (
                <CMSLink
                  key={index}
                  {...link}
                  className={`${classes.button} ${index === 0 ? classes.primary : classes.secondary
                    }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
